import { InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { compact, first, uniq } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, UseFieldArrayRemove, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { Button } from '@/components/form/baseInputs/Button';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  SelectInput,
  SelectInputProps,
} from '@/components/form/baseInputs/SelectInput/SelectInput';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwareCardRepeater } from '@/components/form/formAwareInputs/FormAwareCardRepeater/FormAwareCardRepeater';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import {
  FormAwareFormattedNumberInput,
  FormAwareFormattedNumberInputProps,
} from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { AdditionalItemRendererProps } from '@/components/form/formAwareInputs/FormAwareInput.types';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { Card, CardVariant } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form/hooks';
import { BOTH_GRANTORS_SENTINEL } from '@/hooks/useDualGrantorOptions';
import { SuggestableFormAwareInput } from '@/modules/aiSuggestions/components/forms/SuggestableFormAwareInput';
import { AssetIntegrationProviders } from '@/modules/assetProviderIntegrations/shared/constants';
import { EmoneySyncedFieldsCallout } from '@/modules/assetProviderIntegrations/shared/EmoneySyncedFieldsCallout';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { DocumentUploaderWithList } from '@/modules/documents/DocumentUploaderWithList/DocumentUploaderWithList';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AiSuggestionKind,
  DocumentType,
  InsurancePolicyKind,
  InsurancePolicyPremiumFrequency,
  InsurancePolicyPremiumPaymentSourceKind,
  InsurancePolicyPremiumPaymentTransferTaxKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import { getGrantorOptionsFromData } from '../inputs/PrimaryClientDropdown/PrimaryClientDropdown.utils';
import {
  defaultPolicy,
  Fields as InsurancePolicyDetailsSubformFields,
} from '../InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { IntegrationInsuranceSelect } from './components/IntegrationInsuranceSelect';
import { useInsurancePolicyDetailsPremiumPaymentSourceOptions } from './hooks/useInsurancePolicyDetailsPremiumPaymentSourceOptions';
import { useInsurancePolicyDetailsPremiumPaymentTransferKindOptions } from './hooks/useInsurancePolicyDetailsPremiumPaymentTransferKindOptions';
import {
  Fields,
  INSURANCE_POLICIES_DETAILS_SUBFORM,
  InsurancePolicyDetails,
  InsurancePolicyDetailsSubformTypePaths,
  InsurancePolicyTypeCopyMap,
  Props,
  VariantType,
} from './InsurancePolicyDetailsSubform.types';
import { InsurancePolicyEmptyState } from './InsurancePolicyEmptyState';

const TERM_LENGTH_OPTIONS: SelectInputProps['options'] = new Array(8)
  .fill(null, 0, 8)
  .map((_, index) => ({
    value: (index + 1) * 5,
    display: `${(index + 1) * 5} years`,
  }));

const POLICY_OPTIONS: SelectInputProps['options'] = [
  {
    value: InsurancePolicyKind.Whole,
    display: InsurancePolicyTypeCopyMap[InsurancePolicyKind.Whole],
  },
  {
    value: InsurancePolicyKind.Term,
    display: InsurancePolicyTypeCopyMap[InsurancePolicyKind.Term],
  },
  {
    value: InsurancePolicyKind.Universal,
    display: InsurancePolicyTypeCopyMap[InsurancePolicyKind.Universal],
  },
  {
    value: InsurancePolicyKind.Variable,
    display: InsurancePolicyTypeCopyMap[InsurancePolicyKind.Variable],
  },
];

const PREMIUM_FREQUENCY_OPTIONS = [
  {
    display: 'Annual',
    value: InsurancePolicyPremiumFrequency.Annually,
  },
  {
    display: 'Semi-annual',
    value: InsurancePolicyPremiumFrequency.Semiannually,
  },
  {
    display: 'Quarterly',
    value: InsurancePolicyPremiumFrequency.Quarterly,
  },
  {
    display: 'Monthly',
    value: InsurancePolicyPremiumFrequency.Monthly,
  },
];

const getPolicyFieldName =
  (policyIndex: number) =>
  <T extends keyof InsurancePolicyDetails>(
    fieldName: T
  ): `${typeof INSURANCE_POLICIES_DETAILS_SUBFORM}.policies.${typeof policyIndex}.${T}` =>
    `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies.${policyIndex}.${fieldName}` as const satisfies InsurancePolicyDetailsSubformTypePaths;

const EMPTY_PREFILLED_FIELDS = new Set<keyof InsurancePolicyDetails>();

export interface InsurancePolicyFormProps {
  policyIndex: number;
  householdId: string;
  entityId?: string | undefined;
  variant: VariantType;
  remove: UseFieldArrayRemove;
  additionalItemRendererProps?: AdditionalItemRendererProps;
  isHypothetical?: boolean;
  isCreateHypothetical?: boolean;
  /** Hide the document uploader section (e.g. when no entity exists yet) */
  hideDocuments?: boolean;
  /** Hide the premium payment source section (e.g. when no entity exists yet) */
  hidePremiumPaymentSource?: boolean;
  /** When true, marks this policy as sourced from an integration */
  isIntegrationLinked?: boolean;
  /**
   * Set of field names that the integration populated. Only these fields are
   * disabled when isIntegrationLinked is true, so the user can still fill in
   * fields the integration left blank. Passed from the parent when the
   * integration select is hidden (e.g. the linking step drawer).
   */
  initialIntegrationPrefilledFields?: Set<keyof InsurancePolicyDetails>;
  /** When true, hides the integration insurance typeahead select */
  hideIntegrationSelect?: boolean;
  /** Overrides the variants used by the internal cards */
  internalCardVariantOverride?: CardVariant;
}

export const InsurancePolicyForm: FC<InsurancePolicyFormProps> = ({
  policyIndex,
  householdId,
  entityId,
  remove,
  additionalItemRendererProps,
  isHypothetical,
  isCreateHypothetical = false,
  hideDocuments,
  hidePremiumPaymentSource,
  isIntegrationLinked: isIntegrationLinkedProp,
  initialIntegrationPrefilledFields,
  hideIntegrationSelect,
  internalCardVariantOverride,
}) => {
  const { control, formState, setValue } = useFormContext<Fields>();

  const { update } = useFieldArray({
    control,
    name: `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies`,
  });

  const { possibleGrantors, linkedHouseholdAssetProvider } =
    useHouseholdDetailsContext();
  const enableEmoneyAssetsLiabilities = useFeatureFlag(
    'enable_emoney_assets_liabilities'
  );
  const theme = useTheme();
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const canEdit = useCanEditHouseholdData();

  const getFieldName = getPolicyFieldName(policyIndex);

  // When rendering inside a modal/drawer, disable portals to prevent focus/accessibility issues
  const selectMenuProps = useMemo(() => ({ disablePortal: true }), []);

  const policyType = useWatch({
    name: getFieldName('policyType'),
    control,
  });
  const isTermPolicy = policyType === InsurancePolicyKind.Term;
  const isConvertible = useWatch({
    name: getFieldName('isConvertible'),
    control,
  });
  const integrationInsuranceId = useWatch({
    name: getFieldName('integrationInsuranceId'),
    control,
  });
  const isIntegrationLinked =
    isIntegrationLinkedProp ?? !!integrationInsuranceId;

  // Tracks which fields were actually populated by the integration at prefill
  // time. Only these fields get disabled, so the user can still fill in fields
  // the integration left blank. Updated via onPrefill callback from the
  // integration select, or seeded from the parent via prop (drawer case).
  const [integrationPrefilledFields, setIntegrationPrefilledFields] = useState(
    () => initialIntegrationPrefilledFields ?? EMPTY_PREFILLED_FIELDS
  );
  useEffect(() => {
    if (!initialIntegrationPrefilledFields) return;

    setIntegrationPrefilledFields(initialIntegrationPrefilledFields);
  }, [initialIntegrationPrefilledFields]);
  const isFieldPrefilledByIntegration = (field: keyof InsurancePolicyDetails) =>
    isIntegrationLinked && integrationPrefilledFields.has(field);

  const policyHolderIDs =
    useWatch({
      name: getFieldName('policyHolderIDs'),
      control,
    }) || [];

  const [showDocumentUploadError, setShowDocumentUploadError] =
    useState<boolean>(false);
  const formDocuments: string[] = useWatch({
    name: getFieldName('documentIds'),
  });

  const [documentIds, setDocumentIds] = useState<string[]>(formDocuments);
  const isSurvivorshipPolicy = !isTermPolicy && isConvertible;

  const dirtyPolicy =
    formState.dirtyFields[INSURANCE_POLICIES_DETAILS_SUBFORM]?.policies?.[
      policyIndex
    ] || {};
  const dirtyKeys = Object.keys(
    dirtyPolicy
  ) as (keyof InsurancePolicyDetails)[];
  const validateFields =
    !!dirtyKeys.find((item) => dirtyPolicy?.[item]) && !!policyType;

  // Include non-standard term lengths (e.g. 12 years from an integration) in
  // the options so the select can display them properly.
  const termLength = useWatch({ name: getFieldName('termLength'), control });
  const termLengthOptions = useMemo(() => {
    if (
      typeof termLength === 'number' &&
      !TERM_LENGTH_OPTIONS.some((o) => o.value === termLength)
    ) {
      return [
        ...TERM_LENGTH_OPTIONS,
        { value: termLength, display: `${termLength} years` },
      ];
    }
    return TERM_LENGTH_OPTIONS;
  }, [termLength]);

  const grantorOptions: TypeaheadSelectInputOption<string>[] =
    getGrantorOptionsFromData(possibleGrantors ?? [], policyHolderIDs);
  const showIntegrationSelect =
    !hideIntegrationSelect &&
    !isHypothetical &&
    !isCreateHypothetical &&
    enableEmoneyAssetsLiabilities &&
    linkedHouseholdAssetProvider === AssetIntegrationProviders.EMONEY;

  return (
    <>
      {showIntegrationSelect && (
        <FormLayoutRow>
          <FormLayoutItem>
            <IntegrationInsuranceSelect
              policyIndex={policyIndex}
              provider={linkedHouseholdAssetProvider!}
              onPrefill={setIntegrationPrefilledFields}
              policyHolderOptions={grantorOptions}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {isIntegrationLinked && (
        <FormLayoutRow>
          <FormLayoutItem>
            <EmoneySyncedFieldsCallout />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          {/* need to use a plain select for the custom change handler */}
          <SelectInput
            label="Policy type"
            options={POLICY_OPTIONS}
            disabled={
              disabledFromContext || isFieldPrefilledByIntegration('policyType')
            }
            MenuProps={selectMenuProps}
            onChange={(event) => {
              const newValue = event.target.value as InsurancePolicyKind | '';
              // if blank, clear out the policy details for this policy form index
              if (!newValue) {
                update(policyIndex, { ...defaultPolicy, policyType: '' });
                return;
              }
              setValue(getFieldName('policyType'), newValue, {
                shouldDirty: true,
                shouldTouch: true,
              });
              setValue(getFieldName('isConvertible'), false);
              const activePolicyHolderIDs = compact(policyHolderIDs);
              if (newValue === InsurancePolicyKind.Term) {
                // if newly setting to an term policy, enforce the single-insured restriction
                // by setting the policyHolderIDs array to either the first existing result or a blank string
                setValue(getFieldName('policyHolderIDs'), [
                  first(activePolicyHolderIDs) || '',
                ]);
              } else if (!activePolicyHolderIDs.length) {
                setValue(getFieldName('policyHolderIDs'), ['']);
              }
            }}
            value={policyType}
            required={isCreateHypothetical}
          />
        </FormLayoutItem>
        <FormLayoutItem
          width={6}
          sx={{
            // necessary to align with the top-label text box to the left
            mt: '22px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <FormAwareSwitch
            fieldName={getFieldName('isConvertible')}
            control={control}
            label={
              isTermPolicy
                ? 'Convertible'
                : 'Survivorship / second to die policy'
            }
            labelPosition="right"
          />
        </FormLayoutItem>
      </FormLayoutRow>

      {isTermPolicy && isConvertible && (
        <FormLayoutRow>
          <FormLayoutItem>
            <Card
              variant={internalCardVariantOverride ?? 'filled-callout'}
              sx={{ p: 2 }}
            >
              <FormLayoutRow>
                <FormLayoutItem width={4}>
                  <FormAwareDatePickerInput
                    fieldName={getFieldName('conversionDate')}
                    label="Date of conversion"
                    control={control}
                  />
                </FormLayoutItem>
              </FormLayoutRow>
            </Card>
          </FormLayoutItem>
        </FormLayoutRow>
      )}

      <FormLayoutRow>
        <FormLayoutItem width={8}>
          <FormAwareTextInput
            fieldName={getFieldName('insuranceCarrier')}
            label="Insurance carrier"
            control={control}
          />
        </FormLayoutItem>
        <FormLayoutItem width={4}>
          <FormAwareTextInput
            fieldName={getFieldName('policyNumber')}
            label="Policy number"
            control={control}
            disabled={isFieldPrefilledByIntegration('policyNumber')}
          />
        </FormLayoutItem>
      </FormLayoutRow>

      <FormLayoutRow
        sx={
          isTermPolicy
            ? undefined
            : { gap: 2, mb: `${theme.spacing(1)} !important` }
        }
      >
        <FormLayoutItem>
          <FormAwareTypeaheadSelectInput<Fields>
            startAdornment={
              <InputAdornment position="start">
                <User01Icon size={16} />
              </InputAdornment>
            }
            control={control}
            options={grantorOptions}
            fieldName={`${getFieldName('policyHolderIDs')}.0`}
            label="Insured name"
            required={isTermPolicy || validateFields || isCreateHypothetical}
            placeholder="Search insured names"
          />
        </FormLayoutItem>
      </FormLayoutRow>

      {!isTermPolicy && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTypeaheadSelectInput<Fields>
              startAdornment={
                <InputAdornment position="start">
                  <User01Icon size={16} />
                </InputAdornment>
              }
              control={control}
              options={grantorOptions}
              fieldName={`${getFieldName('policyHolderIDs')}.1`}
              label="Insured name"
              required={validateFields && isSurvivorshipPolicy}
              hideLabel
              placeholder="Search insured names"
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}

      <FormLayoutRow>
        <FormLayoutItem width={isTermPolicy ? 4 : 6}>
          <FormAwareCurrencyInput
            control={control}
            label="Death benefit amount"
            fieldName={getFieldName('deathBenefitAmount')}
            required={validateFields || isCreateHypothetical}
            isDecimalJSInput
            disabled={isFieldPrefilledByIntegration('deathBenefitAmount')}
          />
        </FormLayoutItem>
        <FormLayoutItem width={isTermPolicy ? 4 : 6}>
          <FormAwareDatePickerInput
            label="Policy start date"
            fieldName={getFieldName('policyStartDate')}
            control={control}
            required={isTermPolicy && (validateFields || isCreateHypothetical)}
            disabled={isFieldPrefilledByIntegration('policyStartDate')}
          />
        </FormLayoutItem>
        {isTermPolicy && (
          <FormLayoutItem width={4}>
            <FormAwareSelectInput
              label="Term length"
              fieldName={getFieldName('termLength')}
              control={control}
              options={termLengthOptions}
              required
              MenuProps={selectMenuProps}
              disabled={isFieldPrefilledByIntegration('termLength')}
            />
          </FormLayoutItem>
        )}
      </FormLayoutRow>

      <Card
        variant={internalCardVariantOverride ?? 'filled-outline'}
        sx={{ p: 2, mb: 2 }}
      >
        <FormLayoutRow>
          <FormLayoutItem>
            <Typography variant="title-s">Policy premium details</Typography>
          </FormLayoutItem>
        </FormLayoutRow>

        <FormLayoutRow>
          <FormLayoutItem width={isHypothetical ? 6 : 4}>
            <FormAwareCurrencyInput
              label="Premium amount"
              fieldName={getFieldName('premiumAmount')}
              control={control}
              isDecimalJSInput
              disabled={isFieldPrefilledByIntegration('premiumAmount')}
            />
          </FormLayoutItem>
          <FormLayoutItem width={isHypothetical ? 6 : 4}>
            <FormAwareSelectInput
              label="Frequency"
              fieldName={getFieldName('premiumFrequency')}
              control={control}
              options={PREMIUM_FREQUENCY_OPTIONS}
              MenuProps={selectMenuProps}
            />
          </FormLayoutItem>
          {isHypothetical ? null : (
            <FormLayoutItem width={4}>
              <FormAwareDatePickerInput
                label="Initial due date"
                fieldName={getFieldName('premiumInitialDueDate')}
                control={control}
              />
            </FormLayoutItem>
          )}
        </FormLayoutRow>
      </Card>

      {!hidePremiumPaymentSource && (
        <PremiumPaymentSourceForm
          policyIndex={policyIndex}
          isHypothetical={isHypothetical}
          entityId={entityId}
          internalCardVariantOverride={internalCardVariantOverride}
        />
      )}

      {!isTermPolicy && !isCreateHypothetical && (
        <FormLayoutRow>
          <FormLayoutItem width={4}>
            <FormAwareCurrencyInput<Fields>
              fieldName={getFieldName('cashValue')}
              label="Cash value"
              control={control}
              isDecimalJSInput
              disabled={
                isHypothetical || isFieldPrefilledByIntegration('cashValue')
              }
            />
          </FormLayoutItem>
          <FormLayoutItem width={4}>
            <FormAwareDatePickerInput<Fields>
              fieldName={getFieldName('cashValueDate')}
              label="Cash value date"
              control={control}
              disabled={isHypothetical}
            />
          </FormLayoutItem>
          <FormLayoutItem width={4}>
            <FormAwareCurrencyInput<Fields>
              fieldName={getFieldName('loanBalanceOutstanding')}
              label="Loan outstanding"
              control={control}
              isDecimalJSInput
              disabled={isHypothetical}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {!isCreateHypothetical && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTextInput
              label="Notes"
              fieldName={getFieldName('notes')}
              control={control}
              multiline
              rows={3}
              resizable
              disabled={isHypothetical}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {canEdit && !isHypothetical && !hideDocuments && (
        <FormLayoutRow>
          <FormLayoutItem>
            <DocumentUploaderWithList
              variant="compact"
              uploaderLabel=""
              documentIds={documentIds}
              householdId={householdId}
              listDocumentsLike={
                entityId
                  ? {
                      hasEntityWith: [{ id: entityId }],
                      type: DocumentType.InsurancePolicy,
                    }
                  : undefined
              }
              getCreateDocumentInput={(file) => ({
                householdID: householdId,
                name: file.fileName,
                entityID: entityId || undefined,
                type: DocumentType.InsurancePolicy,
                fileID: file.fileId,
              })}
              onDocumentUploadSuccess={({ documentId }) => {
                const uniqueDocumentIds = uniq([...documentIds, documentId]);
                setValue(getFieldName('documentIds'), uniqueDocumentIds);
                setDocumentIds(uniqueDocumentIds);
                setShowDocumentUploadError(false);
              }}
              errorMessage={
                showDocumentUploadError
                  ? 'Could not upload the document'
                  : undefined
              }
              onDocumentUploadError={(error) => {
                diagnostics.error(
                  'Caught error when uploading ILIT document',
                  error
                );
                setShowDocumentUploadError(true);
              }}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {!additionalItemRendererProps?.hideDeleteButton && canEdit && (
        <Stack width="100%" flexDirection="row" justifyContent="end">
          <Button
            variant="destructive-transparent"
            size="sm"
            onClick={() => {
              remove(policyIndex);
            }}
            startIcon={Trash01Icon}
            data-testid="button-remove-repeater-input-item"
          >
            Remove policy
          </Button>
        </Stack>
      )}
    </>
  );
};

export function UI(props: Props) {
  const { householdId, entityId, variant } = props;

  const { control, formState: insurancePoliciesFormState } =
    useFormContext<Fields>();

  const { append } = useFieldArray({
    control,
    name: `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies`,
  });

  const policies = useWatch({
    name: `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies`,
    control,
  });

  const hasPolicies = (policies || []).length > 0;

  const handleAddPolicy = useCallback(() => {
    append(defaultPolicy, { shouldFocus: true });
  }, [append]);

  const shouldValidateCrummeyLetter = useMemo(() => {
    // validate the crummey letter field if:
    // - the user has dirtied any field in the form
    // - there's at least one policy holder
    //   for one policy holder to exist, the user either needs to dirty the form (to newly
    //   select one) or load an existing policy; this is effectively a stand-in for validating
    //   after loading an existing trust
    const hasPolicyHolder = !!(policies || []).find((policy) =>
      policy?.policyHolderIDs?.find((id) => !!id)
    );

    const hasDirtyInsuranceField =
      !!insurancePoliciesFormState.dirtyFields[
        INSURANCE_POLICIES_DETAILS_SUBFORM
      ]?.policies;
    return hasDirtyInsuranceField || hasPolicyHolder;
  }, [insurancePoliciesFormState.dirtyFields, policies]);

  return (
    <Stack direction="column" gap={2} flex={1} sx={{ minHeight: '100%' }}>
      {variant === 'ilit' && (
        <>
          <Typography variant="body-s">
            <b>Tasks are automatically generated</b> using the policy &
            beneficiary details entered below. These tasks will prompt you to
            make annual gift contributions, send Crummey letters, and make
            premium payments.
          </Typography>
          <Card variant="filled-callout" sx={{ p: 2, flexShrink: 0 }}>
            <FormLayoutRow>
              <FormLayoutItem sx={{ width: '240px' }}>
                <SuggestableFormAwareInput<
                  Fields,
                  FormAwareFormattedNumberInputProps<Fields>
                >
                  FormAwareElement={FormAwareFormattedNumberInput}
                  suggestionKind={AiSuggestionKind.CrummeyWithdrawalPeriodDays}
                  fieldName={
                    `${INSURANCE_POLICIES_DETAILS_SUBFORM}.crummeyWithdrawalPeriodDays` as const satisfies InsurancePolicyDetailsSubformTypePaths
                  }
                  label="Crummey withdrawal period"
                  decimalScale={0}
                  control={control}
                  required={shouldValidateCrummeyLetter}
                  isDecimalJSInput
                  fixedDecimalScale
                  endAdornment={<Typography variant="body-xs">days</Typography>}
                />
              </FormLayoutItem>
            </FormLayoutRow>
          </Card>
        </>
      )}
      {hasPolicies ? (
        <FormAwareCardRepeater<InsurancePolicyDetailsSubformFields>
          name={
            `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies` as const satisfies InsurancePolicyDetailsSubformTypePaths
          }
          control={control}
          addAnotherItemText="Add additional policy"
          showRemove={true}
          emptyValue={defaultPolicy}
          allowEmpty={true}
          shouldInit={false}
          render={(
            index,
            fieldArrayProps,
            _id,
            additionalItemRendererProps
          ) => (
            <Card key={_id} variant="filled" sx={{ p: 2 }}>
              <InsurancePolicyForm
                policyIndex={index}
                householdId={householdId}
                entityId={entityId}
                variant={variant}
                remove={fieldArrayProps.remove}
                additionalItemRendererProps={additionalItemRendererProps}
              />
            </Card>
          )}
        />
      ) : (
        <Stack flex={1} justifyContent="center">
          <InsurancePolicyEmptyState onAdd={handleAddPolicy} />
        </Stack>
      )}
    </Stack>
  );
}

export function PremiumPaymentSourceForm({
  policyIndex,
  isHypothetical,
  entityId,
  internalCardVariantOverride,
}: Pick<
  InsurancePolicyFormProps,
  'policyIndex' | 'isHypothetical' | 'entityId' | 'internalCardVariantOverride'
>) {
  const getFieldName = getPolicyFieldName(policyIndex);

  const { possibleGrantors } = useHouseholdDetailsContext();

  // using refs here because these values _should_ be stable within a run
  const bothGrantorIDs = useMemo<string[]>(
    () => compact((possibleGrantors || []).map((grantor) => grantor.id)),
    [possibleGrantors]
  );
  const premiumPaymentGiftingGrantorIDsFieldName = useMemo(
    () => getFieldName('premiumPaymentGiftingGrantorIDs'),
    [getFieldName]
  );

  const { control, setValue } = useFormContext<Fields>();

  // Disable portals for Select menus to prevent focus/accessibility issues in modals
  const selectMenuProps = useMemo(() => ({ disablePortal: true }), []);

  const [premiumPaymentSourceID, premiumPaymentTransferTaxKind] = useWatch({
    name: [
      getFieldName('premiumPaymentSourceID'),
      getFieldName('premiumPaymentTransferTaxKind'),
    ],
    control,
  });
  const previousPremiumPaymentTransferTaxKind = usePrevious(
    premiumPaymentTransferTaxKind
  );

  useEffect(() => {
    if (!premiumPaymentTransferTaxKind) return;
    if (!premiumPaymentSourceID) return;
    if (premiumPaymentTransferTaxKind === previousPremiumPaymentTransferTaxKind)
      return;

    if (
      premiumPaymentTransferTaxKind ===
      InsurancePolicyPremiumPaymentTransferTaxKind.AnnualExclusionGiftWithCrummeyPowers
    ) {
      setValue(premiumPaymentGiftingGrantorIDsFieldName, []);
    } else if (premiumPaymentTransferTaxKind === BOTH_GRANTORS_SENTINEL) {
      setValue(premiumPaymentGiftingGrantorIDsFieldName, bothGrantorIDs);
    } else {
      setValue(premiumPaymentGiftingGrantorIDsFieldName, [
        premiumPaymentTransferTaxKind,
      ]);
    }
  }, [
    bothGrantorIDs,
    premiumPaymentGiftingGrantorIDsFieldName,
    premiumPaymentSourceID,
    premiumPaymentTransferTaxKind,
    previousPremiumPaymentTransferTaxKind,
    setValue,
  ]);

  // When the funding source is cleared or set to Self, clear the transfer tax
  // kind and gifting grantors so we don't submit stale values.
  const showTransferTaxKind =
    !!premiumPaymentSourceID &&
    premiumPaymentSourceID !== entityId &&
    premiumPaymentSourceID !== InsurancePolicyPremiumPaymentSourceKind.Self;

  const previousShowTransferTaxKind = usePrevious(showTransferTaxKind);
  useEffect(() => {
    // Only clear when transitioning from shown to hidden, to avoid
    // re-render loops from repeatedly setting already-empty values.
    if (previousShowTransferTaxKind && !showTransferTaxKind) {
      setValue(getFieldName('premiumPaymentTransferTaxKind'), '');
      setValue(premiumPaymentGiftingGrantorIDsFieldName, []);
    }
  }, [
    showTransferTaxKind,
    previousShowTransferTaxKind,
    setValue,
    getFieldName,
    premiumPaymentGiftingGrantorIDsFieldName,
  ]);

  const { options: premiumPaymentSourceOptions } =
    useInsurancePolicyDetailsPremiumPaymentSourceOptions({
      entityId,
      runQuery: true,
    });
  const premiumPaymentTransferKindOptions =
    useInsurancePolicyDetailsPremiumPaymentTransferKindOptions(isHypothetical);

  if (isHypothetical) return null;

  return (
    <FormLayoutRow>
      <FormLayoutItem>
        <Card
          variant={internalCardVariantOverride ?? 'filled-outline'}
          sx={{ p: 2 }}
        >
          <FormLayoutRow>
            <FormLayoutItem>
              <Typography variant="title-s">Premium payment details</Typography>
            </FormLayoutItem>
          </FormLayoutRow>

          <FormLayoutRow>
            <FormLayoutItem width={12}>
              <FormAwareTypeaheadSelectInput<Fields>
                label="Funding source"
                fieldName={getFieldName('premiumPaymentSourceID')}
                control={control}
                options={premiumPaymentSourceOptions}
                groupBy={(option) => option.groupName ?? ''}
                disabled={isHypothetical}
                contextualHelp={
                  isHypothetical ? undefined : (
                    <PopperContent body="If another entity is making annual gifts to this entity to fund policy premium payments, specify the funding entity here" />
                  )
                }
              />
            </FormLayoutItem>
          </FormLayoutRow>

          {showTransferTaxKind && (
            <FormLayoutRow>
              <FormLayoutItem width={12}>
                <FormAwareSelectInput<Fields>
                  label="Type of transfer (gift tax purposes)"
                  fieldName={getFieldName('premiumPaymentTransferTaxKind')}
                  control={control}
                  options={premiumPaymentTransferKindOptions}
                  required
                  MenuProps={selectMenuProps}
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
        </Card>
      </FormLayoutItem>
    </FormLayoutRow>
  );
}
